import { useCallback, useEffect, useRef, useState } from 'react';

export function useDebounce<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return debounced;
}

export function useDebouncedCallback<A extends unknown[]>(fn: (...args: A) => void, ms: number): (...args: A) => void {
  const fnRef = useRef(fn);
  useEffect(() => { fnRef.current = fn; }, [fn]);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  return useCallback((...args: A) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => fnRef.current(...args), ms);
  }, [ms]);
}

export function useThrottle<T>(value: T, ms: number): T {
  const [throttled, setThrottled] = useState(value);
  const lastRun = useRef(Date.now());
  useEffect(() => {
    const elapsed = Date.now() - lastRun.current;
    if (elapsed >= ms) {
      lastRun.current = Date.now();
      setThrottled(value);
      return undefined;
    }
    const t = setTimeout(() => {
      lastRun.current = Date.now();
      setThrottled(value);
    }, ms - elapsed);
    return () => clearTimeout(t);
  }, [value, ms]);
  return throttled;
}

export function useTimeout(callback: () => void, delay: number | null): void {
  const cbRef = useRef(callback);
  useEffect(() => { cbRef.current = callback; }, [callback]);
  useEffect(() => {
    if (delay === null) return undefined;
    const t = setTimeout(() => cbRef.current(), delay);
    return () => clearTimeout(t);
  }, [delay]);
}

export function useInterval(callback: () => void, delay: number | null): void {
  const cbRef = useRef(callback);
  useEffect(() => { cbRef.current = callback; }, [callback]);
  useEffect(() => {
    if (delay === null) return undefined;
    const id = setInterval(() => cbRef.current(), delay);
    return () => clearInterval(id);
  }, [delay]);
}

export interface UseCountdownResult {
  remaining: number;
  done: boolean;
}

export function useCountdown(targetMs: number, tickMs = 1000): UseCountdownResult {
  const [remaining, setRemaining] = useState(Math.max(0, targetMs - Date.now()));
  useEffect(() => {
    setRemaining(Math.max(0, targetMs - Date.now()));
    const id = setInterval(() => {
      const r = Math.max(0, targetMs - Date.now());
      setRemaining(r);
      if (r === 0) clearInterval(id);
    }, tickMs);
    return () => clearInterval(id);
  }, [targetMs, tickMs]);
  return { remaining, done: remaining === 0 };
}
